'use client';

import SectionIntegration from '@/components/sectionIntegration/SectionIntegration';
import TransformYourStrategy from '@/components/blocks/TransformYourStrategy';
import BuildSmarter from '@/components/blocks/BuildSmarter';
import Testimonial from '@/components/testimonial/Testimonial';
import GrowWithLouiseBot from '@/components/blocks/GrowWithLouiseBot';
import { useTranslation } from '@/hooks/useTranslation';
import SectionTrustedBy from '@/components/sectionTrustedBy/SectionTrustedBy';
import HeroHome from '@/components/blocks/HeroHome';
import { FiBarChart, FiTrendingDown, FiTrendingUp } from 'react-icons/fi';
import React from 'react';
import TopThreeBenefits from '@/components/blocks/TopThreeBenefits';
import { BiDollar } from 'react-icons/bi';
import { CtaTargetType } from '@/components/buttons/LouiseButton';
import { TbHexagonNumber1 } from 'react-icons/tb';
import ChatbotTypes from '@/components/blocks/ChatbotTypes';
import { AnimatedGradientWrapper } from '@/components/background/AnimatedGradientWrapper';

interface PageContentProps {
  locale: string;
}

export default function PageContent({ locale }: PageContentProps) {
  const { t } = useTranslation(locale);

  const topThreeFeature = {
    content: {
      title: t('Home/problems/section/title'),
      subtitle: t('Home/problems/section/subtitle'),
      badge: t('Home/problems/section/badge'),
    },
    features: [
      {
        icon: FiTrendingDown,
        title: t('Home/problems/differentiation/title'),
        description: t('Home/problems/differentiation/description'),
        button: t('Home/problems/differentiation/button'),
        imgSrc: 'https://cdn.1cdn.app/application/LOUISEBOT/25070717080280_frustrated.png',
        color: 'indigo',
        gradient: true,
        metric: t('Home/problems/differentiation/metric'),
        metricLabel: t('Home/problems/differentiation/metric-label'),
        ctaTarget: 'register' as CtaTargetType,
      },
      {
        icon: FiBarChart,
        title: t('Home/problems/engagement/title'),
        description: t('Home/problems/engagement/description'),
        button: t('Home/problems/engagement/button'),
        imgSrc: 'https://cdn.1cdn.app/application/LOUISEBOT/25070708164734_innovation.png',
        color: 'blue',
        gradient: true,
        metric: t('Home/problems/engagement/metric'),
        metricLabel: t('Home/problems/engagement/metric-label'),
        ctaTarget: 'register' as CtaTargetType,
      },
      {
        icon: BiDollar,
        title: t('Home/problems/revenue/title'),
        description: t('Home/problems/revenue/description'),
        button: t('Home/problems/revenue/button'),
        imgSrc: 'https://cdn.1cdn.app/application/LOUISEBOT/25070708164612_earn.png',
        color: 'green',
        gradient: true,
        metric: t('Home/problems/revenue/metric'),
        metricLabel: t('Home/problems/revenue/metric-label'),
        ctaTarget: 'roi-calculator' as CtaTargetType,
      },
    ],
  };

  const topThreeBenefits = {
    content: {
      title: t('Home/benefit/section/title'),
      subtitle: t('Home/benefit/section/subtitle'),
      badge: t('Home/benefit/section/badge'),
    },
    features: [
      {
        icon: BiDollar,
        title: t('Home/benefit/column-1/title'),
        description: t('Home/benefit/column-1/description'),
        button: t('Home/benefit/column-1/button'),
        imgSrc: 'https://cdn.1cdn.app/application/LOUISEBOT/25070722330825_earn.png',
        color: 'purple',
        gradient: true,
        metric: t('Home/benefit/column-1/metric'),
        metricLabel: t('Home/benefit/column-1/metric-label'),
        ctaTarget: 'revenue-calculator' as CtaTargetType,
      },
      {
        icon: TbHexagonNumber1,
        title: t('Home/benefit/column-2/title'),
        description: t('Home/benefit/column-2/description'),
        button: t('Home/benefit/column-2/button'),
        imgSrc: 'https://cdn.1cdn.app/application/LOUISEBOT/25070722385083_specialiste.png',
        color: 'blue',
        gradient: true,
        metric: t('Home/benefit/column-2/metric'),
        metricLabel: t('Home/benefit/column-2/metric-label'),
        ctaTarget: 'register' as CtaTargetType,
      },
      {
        icon: FiTrendingUp,
        title: t('Home/benefit/column-3/title'),
        description: t('Home/benefit/column-3/description'),
        button: t('Home/benefit/column-3/button'),
        imgSrc: 'https://cdn.1cdn.app/application/LOUISEBOT/25070722330848_retention.png',
        color: 'green',
        gradient: true,
        metric: t('Home/benefit/column-3/metric'),
        metricLabel: t('Home/benefit/column-3/metric-label'),
        ctaTarget: 'register' as CtaTargetType,
      },
    ],
  };

  return (
    <div className="tw-relative tw-overflow-hidden">
      {/* Hero Section */}
      <HeroHome locale={locale} />

      <AnimatedGradientWrapper>
        <div className="tw-relative tw-z-10">
          <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
            {/* Features Grid - With background but content in container  */}
            <TopThreeBenefits
              title={topThreeFeature.content.title}
              subtitle={topThreeFeature.content.subtitle}
              badge={topThreeFeature.content.badge}
              features={topThreeFeature.features}
              locale={locale}
            />

            {/* Features Deep Dive */}
            <BuildSmarter locale={locale} />

            {/* Choose Chatbot type */}
            <ChatbotTypes locale={locale} />

            {/* Approach Selection */}
            <TopThreeBenefits
              title={topThreeBenefits.content.title}
              subtitle={topThreeBenefits.content.subtitle}
              badge={topThreeBenefits.content.badge}
              features={topThreeBenefits.features}
              locale={locale}
            />

            {/* Strategy Section */}
            <TransformYourStrategy locale={locale} />

            {/* Trust Section */}
            <SectionTrustedBy locale={locale} />

            {/* Testimonial Section */}
            <Testimonial locale={locale} />

            {/* Testimonials Section */}
            <SectionIntegration locale={locale} />

            <GrowWithLouiseBot locale={locale} />
          </div>
        </div>
      </AnimatedGradientWrapper>
    </div>
  );
}
